import "./global.css"

// Minimal, frame-less Patchwork site: no account/frame, no folder browser. It
// stands up the repo + workers exactly like the bootloader's `bootPatchworkSite`
// does, loads the Tenfold module from /modules.json, then renders the Tenfold
// tool full-screen on a single Tenfold document remembered in
// `localStorage.TENFOLD_URL` (created on first run).

import {
  initializeWasm,
  isValidAutomergeUrl,
  MessageChannelNetworkAdapter,
  Repo,
} from "@automerge/vanillajs/slim"
import { IndexedDBWorkerStorageAdapter } from "@automerge/automerge-repo-storage-indexeddb/IndexedDBWorkerStorageAdapter"
// initSync is a wasm-bindgen runtime helper not surfaced in the .d.ts.
// @ts-expect-error
import { initSync as initSubductionSync, MemorySigner } from "@automerge/automerge-subduction/slim"

import { ModuleWatcher } from "@inkandswitch/patchwork-filesystem"
import { registerPatchworkViewElement } from "@inkandswitch/patchwork-elements"
import { registerRepoProviderElement } from "@inkandswitch/patchwork-providers"
import {
  createDocOfDatatype2,
  getRegistry,
  registerPlugins,
  unregisterPlugins,
} from "@inkandswitch/patchwork-plugins"

import setupServiceWorker from "@inkandswitch/patchwork-bootloader"

const TENFOLD_URL_KEY = "TENFOLD_URL"
const TENFOLD_DATATYPE_ID = "inkandswitch/tenfold"
const TENFOLD_TOOL_ID = "inkandswitch/tenfold"

// The user's own tool bundle lives at /modules.json (served from this site).
// Overridable at runtime with `localStorage.defaultToolsUrl`.
const MODULE_SETTINGS_URL =
  globalThis.localStorage?.getItem("defaultToolsUrl") || "/modules.json"

const [automergeWasm, subductionWasm] = await Promise.all([
  fetch("/automerge.wasm?main").then((r) => r.bytes()),
  fetch("/subduction.wasm").then((r) => r.bytes()),
])

await initializeWasm(automergeWasm)
initSubductionSync(subductionWasm)

const sw = await setupServiceWorker()
if (!sw) throw new Error("Failed to set up service worker")

// The initial automerge-worker port arrives over the repo channel.
let resolvePort!: (port: MessagePort) => void
const portPromise = new Promise<MessagePort>((r) => {
  resolvePort = r
})
await sw.subscribeToRepoChannel(resolvePort)
const workerPort = await portPromise

const tabSigner = new MemorySigner()
const repo = new Repo({
  network: [new MessageChannelNetworkAdapter(workerPort)],
  storage: new IndexedDBWorkerStorageAdapter(),
  signer: tabSigner,
  async sharePolicy(peerId: string) {
    return peerId.includes("automerge-worker")
  },
  enableRemoteHeadsGossiping: true,
  peerId: `tenfold-tab-${crypto.randomUUID()}`,
})

;(window as unknown as { repo: Repo }).repo = repo
await repo.networkSubsystem.whenReady()

// Wire the <repo-provider> / <patchwork-view> elements to this repo.
registerRepoProviderElement(repo)

const root = document.getElementById("root")
if (!root) throw new Error("no #root element")
const repoProvider = document.createElement("repo-provider")
root.parentElement!.insertBefore(repoProvider, root)
repoProvider.appendChild(root)

registerPatchworkViewElement({ repo })

// Load the Tenfold module(s) from /modules.json and register their plugins.
const moduleWatcher = new ModuleWatcher(
  repo,
  { system: MODULE_SETTINGS_URL },
  (name, mod) => {
    if (Array.isArray(mod?.plugins)) {
      registerPlugins(mod.plugins, name)
    } else {
      console.warn(`module ${name} has no plugins array`, mod)
    }
  },
  unregisterPlugins
)
await moduleWatcher.doneLoading

// Find-or-create the single Tenfold document this site opens into.
let tenfoldUrl = globalThis.localStorage?.getItem(TENFOLD_URL_KEY)
if (!tenfoldUrl || !isValidAutomergeUrl(tenfoldUrl)) {
  const datatype = await getRegistry("patchwork:datatype").load(TENFOLD_DATATYPE_ID)
  if (!datatype) {
    throw new Error(
      `${TENFOLD_DATATYPE_ID} datatype not registered — is /modules.json loading the Tenfold module?`
    )
  }
  const handle = await createDocOfDatatype2(datatype, repo)
  tenfoldUrl = handle.url
  globalThis.localStorage?.setItem(TENFOLD_URL_KEY, tenfoldUrl)
}

// Render the Tenfold tool on that document, filling the viewport.
root.setAttribute("tool-id", TENFOLD_TOOL_ID)
root.setAttribute("doc-url", tenfoldUrl)

;(window as unknown as { patchwork: unknown }).patchwork = {
  repo,
  packages: moduleWatcher,
  tenfoldUrl,
}
